import { z } from "zod";
import { Id } from "../common/ids.js";
import { Money } from "../common/money.js";
import { Timestamp } from "../common/time.js";
import { defineRoute } from "../http/route.js";
/**
 * The plans every enterprise uses Civentia on (ADR 0023, ENT-7, card 120): the definitions live in
 * `@platform/domain`'s `PLANS`; these are the shapes the API answers in.
 */
export const PlanKey = z.enum(["starter", "growth", "scale"]).meta({ id: "PlanKey" });
export const BillingPeriod = z.enum(["monthly", "yearly"]).meta({ id: "BillingPeriod" });
export const EnterprisePlan = z
    .object({
    plan: PlanKey,
    billing: BillingPeriod.nullable().describe("Null on Starter, which is free"),
    currentPeriodEnd: Timestamp.nullable().describe("When the paid period ends and renews; null on Starter"),
    limits: z.object({
        members: z.number().int().positive().nullable(),
        liveListings: z.number().int().positive().nullable(),
        storageBytes: z.number().int().positive(),
        customRoles: z.boolean(),
    }),
    price: z.object({ monthly: Money, yearly: Money }).describe("In SGD, excluding GST"),
    members: z.number().int().nonnegative().describe("Members active now"),
    enforced: z
        .boolean()
        .describe("Whether the plans' limits and fees are in force; false during the pilot (ADR 0023)"),
})
    .meta({ id: "EnterprisePlan" });
export const SetEnterprisePlan = z
    .object({
    plan: PlanKey,
    billing: BillingPeriod.nullable(),
    currentPeriodEnd: Timestamp.nullable(),
    reason: z.string().trim().min(3).max(2000),
})
    .superRefine((body, context) => {
    const free = body.plan === "starter";
    for (const field of ["billing", "currentPeriodEnd"]) {
        if (free && body[field] !== null)
            context.addIssue({ code: "custom", path: [field], message: "NOT_ALLOWED" });
        if (!free && body[field] === null)
            context.addIssue({ code: "custom", path: [field], message: "REQUIRED" });
    }
})
    .meta({ id: "SetEnterprisePlan" });
export const getEnterprisePlan = defineRoute({
    id: "identity.plan.get",
    method: "GET",
    path: "/v1/enterprise/plan",
    surface: "tenant",
    auth: "user",
    permission: "enterprise:manage",
    marks: ["tenant"],
    summary: "The enterprise's plan, its limits and price, and its members against the limit",
    prd: ["ENT-7"],
    responses: { 200: { description: "The plan", schema: EnterprisePlan } },
    errors: ["AUTH_REQUIRED", "TENANT_REQUIRED", "PERMISSION_DENIED"],
});
const Finance = { surface: "operator", auth: "operator", permission: "ops:finance.invoice" };
export const getOpsEnterprisePlan = defineRoute({
    ...Finance,
    id: "ops.enterprises.plan.get",
    method: "GET",
    path: "/v1/ops/enterprises/{enterpriseId}/plan",
    marks: [],
    summary: "One enterprise's plan, as its admins see it",
    prd: ["ENT-7"],
    request: { params: z.object({ enterpriseId: Id }) },
    responses: { 200: { description: "The plan", schema: EnterprisePlan } },
    errors: ["AUTH_REQUIRED", "PERMISSION_DENIED", "NOT_FOUND"],
});
export const setOpsEnterprisePlan = defineRoute({
    ...Finance,
    id: "ops.enterprises.plan.set",
    method: "PUT",
    path: "/v1/ops/enterprises/{enterpriseId}/plan",
    marks: [],
    summary: "Put an enterprise on a plan, with a reason, audited: Starter has no billing; a paid plan has its billing period and the end of the period paid",
    prd: ["ENT-7"],
    request: { params: z.object({ enterpriseId: Id }), body: SetEnterprisePlan },
    responses: { 200: { description: "The plan", schema: EnterprisePlan } },
    errors: ["AUTH_REQUIRED", "PERMISSION_DENIED", "NOT_FOUND", "VALIDATION_FAILED", "INVALID_TRANSITION"],
});
export const planRoutes = [getEnterprisePlan, getOpsEnterprisePlan, setOpsEnterprisePlan];
//# sourceMappingURL=plan.routes.js.map